import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type PolarisRowAction = {
    id: string;
    label: string;
    description?: string;
    onSelect: () => void;
    disabled?: boolean;
    title?: string;
    testId?: string;
    variant?: "default" | "destructive";
};

type PolarisRowActionsMenuProps = {
    actions: PolarisRowAction[];
    ariaLabel: string;
    "data-testid"?: string;
};

export default function PolarisRowActionsMenu({
    actions,
    ariaLabel,
    "data-testid": dataTestId,
}: PolarisRowActionsMenuProps) {
    const generatedId = useId();
    const menuId = `${generatedId}-menu`;
    const rootRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLUListElement>(null);
    const [open, setOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
    const [dropUp, setDropUp] = useState(false);

    const visibleActions = actions.filter((action) => action.label);
    const isDisabled = visibleActions.length === 0 || visibleActions.every((action) => action.disabled);

    const updateMenuPosition = () => {
        const trigger = triggerRef.current;
        if (!trigger) return;
        const rect = trigger.getBoundingClientRect();
        const estimatedHeight = visibleActions.length * 44 + 12;
        const spaceBelow = window.innerHeight - rect.bottom - 8;
        const spaceAbove = rect.top - 8;
        const openUp = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;
        setDropUp(openUp);
        setMenuStyle({
            position: "fixed",
            right: Math.max(8, window.innerWidth - rect.right),
            minWidth: Math.max(rect.width, 240),
            maxWidth: 320,
            zIndex: 4000,
            ...(openUp
                ? { bottom: window.innerHeight - rect.top + 4, top: "auto" }
                : { top: rect.bottom + 4, bottom: "auto" }),
        });
    };

    useLayoutEffect(() => {
        if (!open) return;
        updateMenuPosition();
        const onReposition = () => updateMenuPosition();
        window.addEventListener("resize", onReposition);
        window.addEventListener("scroll", onReposition, true);
        return () => {
            window.removeEventListener("resize", onReposition);
            window.removeEventListener("scroll", onReposition, true);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- reposition when open/actions change
    }, [open, visibleActions.length]);

    useEffect(() => {
        if (!open) return;
        const onPointerDown = (e: MouseEvent) => {
            const target = e.target as Node;
            if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) {
                return;
            }
            setOpen(false);
        };
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.preventDefault();
                setOpen(false);
                triggerRef.current?.focus();
            }
        };
        const timer = window.setTimeout(() => {
            document.addEventListener("mousedown", onPointerDown);
        }, 0);
        window.addEventListener("keydown", onKeyDown);
        return () => {
            window.clearTimeout(timer);
            document.removeEventListener("mousedown", onPointerDown);
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [open]);

    const handleSelect = (action: PolarisRowAction) => {
        if (action.disabled) return;
        setOpen(false);
        action.onSelect();
    };

    if (visibleActions.length === 0) {
        return null;
    }

    return (
        <div
            className={`pa-row-actions${open ? " is-open" : ""}${dropUp ? " is-drop-up" : ""}`}
            ref={rootRef}
            data-testid={dataTestId}
        >
            <button
                type="button"
                ref={triggerRef}
                className="pa-row-actions-trigger"
                disabled={isDisabled}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-controls={menuId}
                aria-label={ariaLabel}
                onClick={() => {
                    if (isDisabled) return;
                    setOpen((prev) => !prev);
                }}
            >
                <span className="pa-row-actions-trigger-label">Actions</span>
                <span className="pa-row-actions-chevron" aria-hidden="true" />
            </button>

            {open &&
                !isDisabled &&
                createPortal(
                    <ul id={menuId} ref={menuRef} className="pa-row-actions-menu" role="menu" style={menuStyle}>
                        {visibleActions.map((action) => (
                            <li key={action.id} role="none">
                                <button
                                    type="button"
                                    role="menuitem"
                                    className={`pa-row-actions-item${
                                        action.variant === "destructive" ? " is-destructive" : ""
                                    }`}
                                    disabled={action.disabled}
                                    title={action.title}
                                    data-testid={action.testId}
                                    onClick={() => handleSelect(action)}
                                >
                                    <span className="pa-row-actions-item-label">{action.label}</span>
                                    {action.description && (
                                        <span className="pa-row-actions-item-description">{action.description}</span>
                                    )}
                                </button>
                            </li>
                        ))}
                    </ul>,
                    document.body,
                )}
        </div>
    );
}
